// EmployeeDashboard.js v3.0 - Full Timeclock System with Persistence, GPS, Breaks, My Hours
// Submits production logs, equipment checks, time entries to Google Sheets
import React, { useState, useEffect, useCallback } from 'react';
import { LogOut, Clock, Settings, Play, Square, MapPin, ChevronRight, HardHat, Activity, AlertTriangle, Truck, ShieldAlert, Shovel, User, Loader, CheckCircle, Menu, X, Coffee, FileText, RefreshCw, Map, FolderOpen, MoreHorizontal, Sun, Moon, Home } from 'lucide-react';
import { colors } from '../config/constants';
import { fetchWithRedirect } from '../utils/fetchHelpers';
import { getInputStyle } from '../utils/theme';
import { getCurrentPosition } from '../services/gpsService';
import { loadProjectsList } from '../services/mapService';
import NotificationBell from '../components/NotificationBell';
import { getISOWeek, getISOWeekYear, format, parseISO, startOfISOWeek, addDays } from 'date-fns';

// API URLs
// eslint-disable-next-line no-unused-vars
const PORTAL_URL = 'https://script.google.com/macros/s/AKfycbyUHklFqQCDIFzHKVq488fYtAIW1lChNnWV2FWHnvGEr7Eq0oREhDE5CueoBJ6k-xhKOg/exec';
const GATEWAY_URL = 'https://script.google.com/macros/s/AKfycbyFWHLgFOglJ75Y6AGnyme0P00OjFgE_-qrDN9m0spn4HCgcyBpjvMopsB1_l9MDjIctQ/exec';
const GATEWAY_SECRET = 'LYTcomm2026ClaudeGatewaySecretKey99';
const OPERATIONS_SHEET_ID = '1VciM5TqHC5neB7JzpcFkX0qyoyzjBvIS0fKkOXQqnrc';

const TIMECLOCK_STORAGE_KEY = 'lyt_timeclock';

// Generate unique session ID
const generateSessionId = () => {
  const rand = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `TC-${Date.now()}-${rand}`;
};

// Get ISO week string like "2026-W11"
const getWeekString = (date) => {
  return `${getISOWeekYear(date)}-W${String(getISOWeek(date)).padStart(2, '0')}`;
};

// Format time from ISO string to "9:00 AM"
const formatTime = (isoString) => {
  if (!isoString) return '—';
  try {
    return format(parseISO(isoString), 'h:mm a');
  } catch (e) { return '—'; }
};

// Format date from ISO or YYYY-MM-DD to "Mon 3/11"
const formatDateShort = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = dateStr.includes('T') ? parseISO(dateStr) : parseISO(dateStr + 'T00:00:00');
    return format(d, 'EEE M/d');
  } catch (e) { return '—'; }
};

// Submit data to Google Sheet (append)
const submitToSheet = async (sheetName, rowData) => {
  try {
    const text = await fetchWithRedirect(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsAppend',
        params: {
          spreadsheetId: OPERATIONS_SHEET_ID,
          range: `${sheetName}!A:Z`,
          values: [rowData]
        }
      })
    });
    const result = JSON.parse(text);
    return result.success;
  } catch (err) {
    console.error('Submit error:', err);
    return false;
  }
};

// Read from Google Sheet
const readSheet = async (range) => {
  try {
    const text = await fetchWithRedirect(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsRead',
        params: { spreadsheetId: OPERATIONS_SHEET_ID, range }
      })
    });
    const result = JSON.parse(text);
    if (result.success && result.data?.data) {
      return result.data.data;
    }
  } catch (e) { console.error('Read error:', e); }
  return [];
};

// Write to specific cells in Google Sheet
const writeSheetCells = async (range, values) => {
  try {
    const text = await fetchWithRedirect(GATEWAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        secret: GATEWAY_SECRET,
        action: 'sheetsWrite',
        params: {
          spreadsheetId: OPERATIONS_SHEET_ID,
          range,
          values
        }
      })
    });
    const result = JSON.parse(text);
    return result.success;
  } catch (e) { console.error('Write error:', e); return false; }
};

// Get GPS position (non-blocking, returns empty string on failure)
const getGPS = async () => {
  try {
    const pos = await getCurrentPosition();
    return `${pos.lat},${pos.lng}`;
  } catch (e) {
    return '';
  }
};

const EmployeeDashboard = ({ setCurrentPage, loggedInUser, setLoggedInUser, darkMode }) => {
  // Dynamic colors based on theme
  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const accentSecondary = darkMode ? '#ff6b35' : '#28a745';
  const accentError = darkMode ? '#ff6b6b' : '#e85a4f';

  const bgColor = darkMode ? colors.dark : '#f8fafc';
  const cardBg = darkMode ? colors.darkLight : '#ffffff';
  const textColor = darkMode ? '#ffffff' : colors.dark;
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';

  const [activeTab, setActiveTabRaw] = useState(() => {
    try { return sessionStorage.getItem('employee-tab') || 'dashboard'; } catch { return 'dashboard'; }
  });
  const setActiveTab = useCallback((tab) => {
    setActiveTabRaw(prev => {
      if (prev === tab) return prev;
      try { sessionStorage.setItem('employee-tab', tab); } catch { /* noop */ }
      const currentState = window.history.state || {};
      window.history.pushState({ ...currentState, employeeTab: tab }, '');
      return tab;
    });
  }, []);
  const [clockedIn, setClockedIn] = useState(false);
  const [clockTime, setClockTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ show: false, success: false, message: '' });
  const [showVersion, setShowVersion] = useState(false);

  // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  // Timeclock enhanced state
  const [timeclockSession, setTimeclockSession] = useState(null);
  const [onBreak, setOnBreak] = useState(false);
  const [clockInModalOpen, setClockInModalOpen] = useState(false);
  const [clockOutModalOpen, setClockOutModalOpen] = useState(false);
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [clockInProject, setClockInProject] = useState('');
  const [gpsStatus, setGpsStatus] = useState(''); // 'acquiring', 'acquired', 'failed'

  // My Hours state
  const [myHoursData, setMyHoursData] = useState([]);
  const [myHoursLoading, setMyHoursLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(getWeekString(new Date()));

  // Browser back/forward for tab navigation
  useEffect(() => {
    const onPopState = (e) => {
      if (e.state && e.state.employeeTab && e.state.page === 'employee-dashboard') {
        setActiveTabRaw(e.state.employeeTab);
        try { sessionStorage.setItem('employee-tab', e.state.employeeTab); } catch { /* noop */ }
      }
    };
    window.addEventListener('popstate', onPopState);
    if (!window.history.state?.employeeTab) {
      window.history.replaceState({ ...window.history.state, employeeTab: activeTab }, '');
    }
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Restore timeclock from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(TIMECLOCK_STORAGE_KEY);
    if (saved) {
      try {
        const session = JSON.parse(saved);
        setTimeclockSession(session);
        setClockedIn(true);
        setClockTime(session.clockInTime);
        setOnBreak(session.onBreak || false);
        setClockInProject(session.project || '');
      } catch (e) {
        localStorage.removeItem(TIMECLOCK_STORAGE_KEY);
      }
    }
  }, []);

  // Stale session check — verify session is still Active in sheet
  useEffect(() => {
    if (!timeclockSession) return;
    const checkStale = async () => {
      try {
        const rows = await readSheet('TimeEntries!A:M');
        if (rows.length > 1) {
          const dataRows = rows.slice(1);
          const match = dataRows.find(r => r[0] === timeclockSession.sessionId);
          if (match && match[12] === 'Complete') {
            // Session was closed externally (admin correction)
            localStorage.removeItem(TIMECLOCK_STORAGE_KEY);
            setTimeclockSession(null);
            setClockedIn(false);
            setClockTime(null);
            setOnBreak(false);
          }
        }
      } catch (e) { /* silent — don't block the UI */ }
    };
    checkStale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Production log form state
  const [productionForm, setProductionForm] = useState({
    project: '',
    fiberFootage: '',
    splices: '',
    drops: '',
    poles: '',
    hddFootage: '',
    notes: ''
  });

  // Equipment check form state
  const [equipmentForm, setEquipmentForm] = useState({
    vehicleId: '',
    mileage: '',
    issues: '',
    safetyItems: []
  });

  // Toolbox talk form state
  const [toolboxForm, setToolboxForm] = useState({
    project: '',
    topic: '',
    notes: '',
    attendees: ''
  });

  // Incident form state
  const [incidentForm, setIncidentForm] = useState({
    type: '',
    project: '',
    description: '',
    injuries: false,
    photos: []
  });

  // Timer — subtracts break time
  useEffect(() => {
    let interval;
    if (clockedIn && clockTime) {
      interval = setInterval(() => {
        const now = Date.now();
        const totalElapsed = now - clockTime;
        let breakMs = 0;
        if (timeclockSession?.breaks) {
          for (const brk of timeclockSession.breaks) {
            if (brk.end) {
              breakMs += brk.end - brk.start;
            } else {
              breakMs += now - brk.start; // active break
            }
          }
        }
        setElapsedTime(Math.max(0, Math.floor((totalElapsed - breakMs) / 1000)));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [clockedIn, clockTime, timeclockSession, onBreak]);

  const formatElapsed = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const showNotification = (success, message) => {
    setSubmitStatus({ show: true, success, message });
    setTimeout(() => setSubmitStatus({ show: false, success: false, message: '' }), 3000);
  };

  // ========================
  // TIMECLOCK HANDLERS
  // ========================

  const handleClockIn = async () => {
    if (!clockInProject) {
      setClockInModalOpen(true);
      return;
    }

    setLoading(true);
    setGpsStatus('acquiring');

    const gpsString = await getGPS();
    setGpsStatus(gpsString ? 'acquired' : 'failed');

    const sessionId = generateSessionId();
    const now = Date.now();
    const nowISO = new Date(now).toISOString();
    const weekNum = getWeekString(new Date(now));
    const dateStr = format(new Date(now), 'yyyy-MM-dd');

    const session = {
      sessionId,
      clockInTime: now,
      clockInISO: nowISO,
      clockInGPS: gpsString,
      project: clockInProject,
      breaks: [],
      onBreak: false,
    };

    // Save to localStorage FIRST (ensures persistence even if network fails)
    localStorage.setItem(TIMECLOCK_STORAGE_KEY, JSON.stringify(session));
    setTimeclockSession(session);
    setClockedIn(true);
    setClockTime(now);
    setElapsedTime(0);
    setOnBreak(false);
    setClockInModalOpen(false);

    // Append row to sheet
    const success = await submitToSheet('TimeEntries', [
      sessionId,             // A: SessionID
      loggedInUser?.email || '', // B: Email
      loggedInUser?.name || '',  // C: Name
      nowISO,                // D: ClockIn
      gpsString,             // E: ClockInGPS
      '', '', '',            // F-H: ClockOut, ClockOutGPS, TotalHours (filled on clock-out)
      clockInProject,        // I: Project
      '', '', '',            // J-L: BreakMinutes, BreakLog, Notes
      'Active',              // M: Status
      weekNum,               // N: WeekNumber
      dateStr,               // O: Date
      '', '', ''             // P-R: EmployeeConfirmed, ApprovedBy, ApprovedAt
    ]);

    setLoading(false);
    if (success) {
      showNotification(true, 'Clocked in successfully');
    } else {
      showNotification(false, 'Clock-in saved locally — will sync when online');
    }
    setGpsStatus('');
  };

  const handleClockOut = async () => {
    // First click opens modal, second click (from modal) actually clocks out
    if (!clockOutModalOpen) {
      setClockOutModalOpen(true);
      return;
    }

    setLoading(true);
    setGpsStatus('acquiring');

    const gpsString = await getGPS();
    setGpsStatus(gpsString ? 'acquired' : 'failed');

    const session = timeclockSession;
    if (!session) {
      setLoading(false);
      return;
    }

    // End any active break
    let breaks = [...(session.breaks || [])];
    const lastBreak = breaks[breaks.length - 1];
    if (lastBreak && !lastBreak.end) {
      lastBreak.end = Date.now();
    }

    // Calculate break minutes
    let breakMs = 0;
    for (const brk of breaks) {
      if (brk.end) breakMs += brk.end - brk.start;
    }
    const breakMinutes = Math.round(breakMs / 60000);

    const clockOutISO = new Date().toISOString();
    const totalMs = Date.now() - session.clockInTime - breakMs;
    const totalHours = (totalMs / 3600000).toFixed(2);

    // Find the row in the sheet to update
    let updated = false;
    try {
      const rows = await readSheet('TimeEntries!A:A');
      if (rows.length > 1) {
        const rowIndex = rows.findIndex(r => r[0] === session.sessionId);
        if (rowIndex >= 0) {
          const sheetRow = rowIndex + 1; // 1-based (header is row 1, so data starts at 2, but findIndex includes header)
          updated = await writeSheetCells(`TimeEntries!F${sheetRow}:R${sheetRow}`, [[
            clockOutISO,              // F: ClockOut
            gpsString,                // G: ClockOutGPS
            totalHours,               // H: TotalHours
            session.project,          // I: Project (re-write in case it changed)
            String(breakMinutes),     // J: BreakMinutes
            JSON.stringify(breaks),   // K: BreakLog
            clockOutNotes,            // L: Notes
            'Complete',               // M: Status
            getWeekString(new Date(session.clockInTime)), // N: WeekNumber
            format(new Date(session.clockInTime), 'yyyy-MM-dd'), // O: Date
            '', '', ''                // P-R: unchanged
          ]]);
        }
      }
    } catch (e) {
      console.error('Clock-out update error:', e);
    }

    // Fallback: append complete row if update failed
    if (!updated) {
      await submitToSheet('TimeEntries', [
        session.sessionId,
        loggedInUser?.email || '',
        loggedInUser?.name || '',
        session.clockInISO,
        session.clockInGPS,
        clockOutISO,
        gpsString,
        totalHours,
        session.project,
        String(breakMinutes),
        JSON.stringify(breaks),
        clockOutNotes,
        'Complete',
        getWeekString(new Date(session.clockInTime)),
        format(new Date(session.clockInTime), 'yyyy-MM-dd'),
        '', '', ''
      ]);
    }

    // Clean up
    localStorage.removeItem(TIMECLOCK_STORAGE_KEY);
    setTimeclockSession(null);
    setClockedIn(false);
    setClockTime(null);
    setElapsedTime(0);
    setOnBreak(false);
    setClockOutModalOpen(false);
    setClockOutNotes('');
    setClockInProject('');
    setLoading(false);
    setGpsStatus('');

    showNotification(true, `Clocked out — ${totalHours} hours logged`);
  };

  const handleStartBreak = () => {
    if (!timeclockSession) return;
    const updated = {
      ...timeclockSession,
      breaks: [...timeclockSession.breaks, { start: Date.now() }],
      onBreak: true
    };
    setTimeclockSession(updated);
    setOnBreak(true);
    localStorage.setItem(TIMECLOCK_STORAGE_KEY, JSON.stringify(updated));
    showNotification(true, 'Break started');
  };

  const handleEndBreak = () => {
    if (!timeclockSession) return;
    const breaks = [...timeclockSession.breaks];
    const lastBreak = breaks[breaks.length - 1];
    if (lastBreak && !lastBreak.end) {
      lastBreak.end = Date.now();
    }
    const updated = { ...timeclockSession, breaks, onBreak: false };
    setTimeclockSession(updated);
    setOnBreak(false);
    localStorage.setItem(TIMECLOCK_STORAGE_KEY, JSON.stringify(updated));
    showNotification(true, 'Break ended');
  };

  const handleSwitchProject = async (newProject) => {
    if (!timeclockSession || !newProject) return;
    const updated = { ...timeclockSession, project: newProject };
    setTimeclockSession(updated);
    setClockInProject(newProject);
    localStorage.setItem(TIMECLOCK_STORAGE_KEY, JSON.stringify(updated));

    // Update sheet column I
    try {
      const rows = await readSheet('TimeEntries!A:A');
      if (rows.length > 1) {
        const rowIndex = rows.findIndex(r => r[0] === timeclockSession.sessionId);
        if (rowIndex >= 0) {
          await writeSheetCells(`TimeEntries!I${rowIndex + 1}`, [[newProject]]);
        }
      }
    } catch (e) { /* silent */ }

    showNotification(true, `Switched to ${newProject}`);
  };

  // ========================
  // MY HOURS
  // ========================

  const fetchMyHours = useCallback(async () => {
    setMyHoursLoading(true);
    try {
      const rows = await readSheet('TimeEntries!A:R');
      if (rows.length > 1) {
        const dataRows = rows.slice(1); // skip header
        const myRows = dataRows.filter(r => r[1] === loggedInUser?.email);
        setMyHoursData(myRows);
      }
    } catch (e) {
      console.error('Fetch my hours error:', e);
    }
    setMyHoursLoading(false);
  }, [loggedInUser?.email]);

  const confirmMyHours = async (weekStr) => {
    setLoading(true);
    try {
      const rows = await readSheet('TimeEntries!A:R');
      if (rows.length > 1) {
        for (let i = 1; i < rows.length; i++) {
          const r = rows[i];
          if (r[1] === loggedInUser?.email && r[13] === weekStr && r[12] === 'Complete' && r[15] !== 'Confirmed') {
            await writeSheetCells(`TimeEntries!P${i + 1}`, [['Confirmed']]);
          }
        }
      }
      showNotification(true, `Hours confirmed for ${weekStr}`);
      fetchMyHours();
    } catch (e) {
      showNotification(false, 'Failed to confirm hours');
    }
    setLoading(false);
  };

  // ========================
  // FORM HANDLERS (unchanged)
  // ========================

  const handleProductionSubmit = async () => {
    if (!productionForm.project) {
      showNotification(false, 'Please select a project');
      return;
    }
    setLoading(true);
    const success = await submitToSheet('Production Logs', [
      new Date().toISOString(),
      loggedInUser?.email || '',
      loggedInUser?.name || '',
      productionForm.project,
      productionForm.fiberFootage || '0',
      productionForm.splices || '0',
      productionForm.drops || '0',
      productionForm.poles || '0',
      productionForm.hddFootage || '0',
      productionForm.notes,
      'Submitted'
    ]);
    setLoading(false);
    if (success) {
      showNotification(true, 'Production log submitted successfully!');
      setProductionForm({ project: '', fiberFootage: '', splices: '', drops: '', poles: '', hddFootage: '', notes: '' });
    } else {
      showNotification(false, 'Failed to submit. Please try again.');
    }
  };

  const handleEquipmentSubmit = async () => {
    if (!equipmentForm.vehicleId) {
      showNotification(false, 'Please enter vehicle ID');
      return;
    }
    setLoading(true);
    const success = await submitToSheet('Equipment Checks', [
      new Date().toISOString(),
      loggedInUser?.email || '',
      loggedInUser?.name || '',
      equipmentForm.vehicleId,
      equipmentForm.mileage || '',
      equipmentForm.safetyItems.join(', '),
      equipmentForm.issues || 'None',
      'Submitted'
    ]);
    setLoading(false);
    if (success) {
      showNotification(true, 'Equipment check submitted!');
      setEquipmentForm({ vehicleId: '', mileage: '', issues: '', safetyItems: [] });
    } else {
      showNotification(false, 'Failed to submit. Please try again.');
    }
  };

  const handleToolboxSubmit = async () => {
    if (!toolboxForm.project || !toolboxForm.topic) {
      showNotification(false, 'Please fill in project and topic');
      return;
    }
    setLoading(true);
    const success = await submitToSheet('Safety Talks', [
      new Date().toISOString(),
      loggedInUser?.email || '',
      loggedInUser?.name || '',
      toolboxForm.project,
      toolboxForm.topic,
      toolboxForm.notes,
      toolboxForm.attendees,
      'Completed'
    ]);
    setLoading(false);
    if (success) {
      showNotification(true, 'Toolbox talk signed!');
      setToolboxForm({ project: '', topic: '', notes: '', attendees: '' });
    } else {
      showNotification(false, 'Failed to submit. Please try again.');
    }
  };

  const handleIncidentSubmit = async () => {
    if (!incidentForm.type || !incidentForm.description) {
      showNotification(false, 'Please fill in type and description');
      return;
    }
    setLoading(true);
    const success = await submitToSheet('Incidents', [
      new Date().toISOString(),
      loggedInUser?.email || '',
      loggedInUser?.name || '',
      incidentForm.type,
      incidentForm.project,
      incidentForm.description,
      incidentForm.injuries ? 'Yes' : 'No',
      'Pending Review'
    ]);
    setLoading(false);
    if (success) {
      showNotification(true, 'Incident report submitted! Supervisor notified.');
      setIncidentForm({ type: '', project: '', description: '', injuries: false, photos: [] });
    } else {
      showNotification(false, 'Failed to submit. Please try again.');
    }
  };

  const handleLogout = () => {
    setLoggedInUser(null);
    setCurrentPage('portal-login');
  };

  // Form styles
  const formInputStyle = getInputStyle(darkMode);
  const formSelectStyle = { ...formInputStyle, cursor: 'pointer' };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Clock },
    { id: 'my-hours', label: 'My Hours', icon: FileText },
    { id: 'profile', label: 'My Profile', icon: User, external: 'profile' },
    { id: 'work-map', label: 'Work Map', icon: MapPin, external: 'work-map' },
    { id: 'project-map', label: 'Project Map', icon: MapPin, external: 'project-map' },
    { id: 'potholes', label: 'Pothole Docs', icon: Shovel, external: 'potholes' },
    { id: 'production', label: 'Daily Production', icon: Activity },
    { id: 'equipment', label: 'Equipment Check', icon: Truck },
    { id: 'safety', label: 'Safety / Toolbox', icon: HardHat },
    { id: 'incidents', label: 'Incident Reports', icon: ShieldAlert },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'user-guide', label: 'User Guide', icon: HardHat, external: 'user-guide' },
  ];

  const bottomTabs = [
    { id: 'dashboard', label: 'Home', icon: Home },
    { id: 'my-hours', label: 'Hours', icon: FileText },
    { id: 'production', label: 'Production', icon: Activity },
    { id: 'safety', label: 'Safety', icon: HardHat },
    { id: 'more', label: 'More', icon: MoreHorizontal },
  ];

  // Projects for dropdown + My Projects card
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectsData, setProjectsData] = useState([]);

  // Fetch projects from database
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projects = await loadProjectsList();
        if (projects.length > 0) {
          setProjectOptions(projects.map(p => p.name || p.id));
          setProjectsData(projects);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    };
    fetchProjects();
  }, []);

  // ========================
  // RENDER: DASHBOARD
  // ========================

  const renderDashboard = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>
          Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}, {loggedInUser?.name?.split(' ')[0] || 'Team Member'}!
        </h2>
        <p style={{ color: mutedColor }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      {/* Time Clock Card */}
      <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '32px', marginBottom: '24px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: textColor }}>
          <Clock size={24} color={accentPrimary} /> Time Clock
        </h3>

        {clockedIn ? (
          <div>
            {/* Status + Timer */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: onBreak ? '#f59e0b' : accentSecondary, animation: onBreak ? 'none' : 'pulse 2s infinite' }} />
              <p style={{ color: onBreak ? '#f59e0b' : accentSecondary, fontWeight: '600', fontSize: '1.1rem' }}>
                {onBreak ? 'ON BREAK' : 'Clocked In'}
              </p>
            </div>

            <p style={{ fontSize: '2.5rem', fontWeight: '700', fontFamily: 'monospace', color: onBreak ? '#f59e0b' : accentPrimary, marginBottom: '8px' }}>
              {formatElapsed(elapsedTime)}
            </p>

            {/* Project tag */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <span style={{ backgroundColor: `${accentPrimary}20`, color: accentPrimary, padding: '4px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '500' }}>
                {clockInProject || 'No project'}
              </span>
              {timeclockSession?.clockInGPS && (
                <span style={{ backgroundColor: `${accentSecondary}20`, color: accentSecondary, padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem' }}>
                  <MapPin size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />GPS
                </span>
              )}
            </div>

            {/* Break info */}
            {timeclockSession?.breaks?.length > 0 && (
              <p style={{ color: mutedColor, fontSize: '0.85rem', marginBottom: '16px' }}>
                {timeclockSession.breaks.length} break{timeclockSession.breaks.length > 1 ? 's' : ''} taken
              </p>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {onBreak ? (
                <button onClick={handleEndBreak} style={{ padding: '14px 24px', fontSize: '1rem', fontWeight: '600', backgroundColor: accentSecondary, color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Play size={18} /> End Break
                </button>
              ) : (
                <button onClick={handleStartBreak} style={{ padding: '14px 24px', fontSize: '1rem', fontWeight: '600', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Coffee size={18} /> Start Break
                </button>
              )}

              <button onClick={handleClockOut} disabled={loading} style={{ padding: '14px 24px', fontSize: '1rem', fontWeight: '600', backgroundColor: accentError, color: '#fff', border: 'none', borderRadius: '12px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: loading ? 0.7 : 1 }}>
                {loading ? <Loader size={18} /> : <Square size={18} />} Clock Out
              </button>
            </div>

            {/* Project switch */}
            <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <select
                value={clockInProject}
                onChange={(e) => handleSwitchProject(e.target.value)}
                style={{ ...formSelectStyle, fontSize: '0.85rem', padding: '8px 12px', maxWidth: '250px' }}
              >
                <option value="">Switch Project...</option>
                {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: mutedColor, marginBottom: '16px' }}>You are not clocked in</p>

            {/* Project selector + Clock In button */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={clockInProject}
                onChange={(e) => setClockInProject(e.target.value)}
                style={{ ...formSelectStyle, flex: '1', minWidth: '200px', maxWidth: '300px' }}
              >
                <option value="">Select Project</option>
                {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>

              <button onClick={handleClockIn} disabled={loading} style={{ padding: '16px 32px', fontSize: '1.1rem', fontWeight: '600', backgroundColor: accentSecondary, color: '#fff', border: 'none', borderRadius: '12px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px', opacity: loading ? 0.7 : 1 }}>
                {loading ? <Loader size={18} /> : <Play size={20} />} Clock In
              </button>
            </div>

            {gpsStatus && (
              <p style={{ color: gpsStatus === 'acquired' ? accentSecondary : gpsStatus === 'failed' ? accentError : mutedColor, fontSize: '0.8rem', marginTop: '8px' }}>
                {gpsStatus === 'acquiring' ? 'Getting GPS...' : gpsStatus === 'acquired' ? 'GPS captured' : 'GPS unavailable'}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Clock-In Modal */}
      {clockInModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '100%' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '20px', color: textColor }}>Start Your Shift</h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Select Project</label>
              <select
                value={clockInProject}
                onChange={(e) => setClockInProject(e.target.value)}
                style={formSelectStyle}
              >
                <option value="">Choose a project...</option>
                {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setClockInModalOpen(false)} style={{ flex: 1, padding: '12px', backgroundColor: 'transparent', border: `1px solid ${mutedColor}`, borderRadius: '8px', color: textColor, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={() => { if (clockInProject) handleClockIn(); }}
                disabled={!clockInProject || loading}
                style={{ flex: 1, padding: '12px', backgroundColor: accentSecondary, color: '#fff', border: 'none', borderRadius: '8px', cursor: !clockInProject ? 'not-allowed' : 'pointer', opacity: !clockInProject ? 0.5 : 1 }}
              >
                Start Shift
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clock-Out Modal */}
      {clockOutModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '32px', maxWidth: '450px', width: '100%' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '20px', color: textColor }}>End Your Shift</h3>

            {/* Summary */}
            <div style={{ backgroundColor: `${accentPrimary}10`, borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <p style={{ color: mutedColor, fontSize: '0.8rem' }}>Time Worked</p>
                  <p style={{ color: textColor, fontWeight: '600', fontFamily: 'monospace' }}>{formatElapsed(elapsedTime)}</p>
                </div>
                <div>
                  <p style={{ color: mutedColor, fontSize: '0.8rem' }}>Project</p>
                  <p style={{ color: textColor, fontWeight: '600', fontSize: '0.9rem' }}>{clockInProject || '—'}</p>
                </div>
                <div>
                  <p style={{ color: mutedColor, fontSize: '0.8rem' }}>Breaks</p>
                  <p style={{ color: textColor, fontWeight: '600' }}>{timeclockSession?.breaks?.length || 0}</p>
                </div>
                <div>
                  <p style={{ color: mutedColor, fontSize: '0.8rem' }}>Hours</p>
                  <p style={{ color: textColor, fontWeight: '600' }}>{(elapsedTime / 3600).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Shift Notes (optional)</label>
              <textarea
                value={clockOutNotes}
                onChange={(e) => setClockOutNotes(e.target.value)}
                rows={3}
                placeholder="Finished boring on Main St, hit rock at 4ft..."
                style={{ ...formInputStyle, resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setClockOutModalOpen(false)} style={{ flex: 1, padding: '12px', backgroundColor: 'transparent', border: `1px solid ${mutedColor}`, borderRadius: '8px', color: textColor, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={handleClockOut}
                disabled={loading}
                style={{ flex: 1, padding: '12px', backgroundColor: accentError, color: '#fff', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                {loading ? <Loader size={16} /> : <Square size={16} />}
                Clock Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Projects Card */}
      {projectsData.length > 0 && (
        <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', color: textColor }}>
              <FolderOpen size={20} color={accentPrimary} /> My Projects
            </h3>
            <span style={{ fontSize: '0.85rem', color: mutedColor }}>
              {projectsData.filter(p => p.status === 'Active').length} active
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {projectsData.map((proj) => (
              <div
                key={proj.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', borderRadius: '10px',
                  backgroundColor: darkMode ? '#1e293b' : '#f8fafc',
                  transition: 'background 0.15s', cursor: 'pointer',
                }}
                onClick={() => { window.location.hash = `#project-map?project=${proj.id}`; }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = darkMode ? '#334155' : '#e2e8f0'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = darkMode ? '#1e293b' : '#f8fafc'}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '500', color: textColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {proj.name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: mutedColor }}>
                    {proj.client}{proj.workOrder ? ` — WO ${proj.workOrder}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: '4px', fontSize: '0.7rem',
                    backgroundColor: proj.status === 'Active' ? `${accentSecondary}20` : `${mutedColor}20`,
                    color: proj.status === 'Active' ? accentSecondary : mutedColor,
                  }}>
                    {proj.status}
                  </span>
                  <Map size={16} color={accentPrimary} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {[
          { label: 'Log Production', icon: Activity, color: accentPrimary, tab: 'production' },
          { label: 'Equipment Check', icon: Truck, color: accentSecondary, tab: 'equipment' },
          { label: 'Safety Talk', icon: HardHat, color: '#f59e0b', tab: 'safety' },
          { label: 'Report Incident', icon: ShieldAlert, color: accentError, tab: 'incidents' },
        ].map((action, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(action.tab)}
            style={{
              backgroundColor: cardBg,
              borderRadius: '12px',
              padding: '20px',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'transform 0.2s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: `${action.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <action.icon size={22} color={action.color} />
              </div>
              <span style={{ fontWeight: '500', color: textColor }}>{action.label}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // ========================
  // RENDER: MY HOURS
  // ========================

  const renderMyHours = () => {
    // Filter data for selected week
    const weekData = myHoursData.filter(r => r[13] === selectedWeek);
    const weekTotal = weekData.reduce((sum, r) => sum + (parseFloat(r[7]) || 0), 0);
    const weekBreakTotal = weekData.reduce((sum, r) => sum + (parseInt(r[9]) || 0), 0);
    const allConfirmed = weekData.length > 0 && weekData.every(r => r[15] === 'Confirmed');
    const anyApproved = weekData.some(r => r[16]);

    // Get available weeks from data
    const weekSet = new Set(myHoursData.map(r => r[13]).filter(Boolean));
    const availableWeeks = [...weekSet].sort().reverse();

    // Get week date range for display
    const getWeekRange = (weekStr) => {
      try {
        const [yearStr, weekPart] = weekStr.split('-W');
        const year = parseInt(yearStr);
        const week = parseInt(weekPart);
        const jan4 = new Date(year, 0, 4);
        const weekStart = startOfISOWeek(jan4);
        const adjustedStart = addDays(weekStart, (week - 1) * 7);
        const adjustedEnd = addDays(adjustedStart, 6);
        return `${format(adjustedStart, 'MMM d')} — ${format(adjustedEnd, 'MMM d, yyyy')}`;
      } catch (e) { return weekStr; }
    };

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>My Hours</h2>
            <p style={{ color: mutedColor }}>View your shifts and weekly totals</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              style={{ ...formSelectStyle, fontSize: '0.9rem', padding: '8px 12px' }}
            >
              {availableWeeks.length === 0 && <option value={selectedWeek}>{selectedWeek}</option>}
              {availableWeeks.map(w => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
            <button onClick={fetchMyHours} disabled={myHoursLoading} style={{ padding: '8px 16px', backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}>
              {myHoursLoading ? <Loader size={14} /> : <RefreshCw size={14} />}
              Refresh
            </button>
          </div>
        </div>

        {/* Week range display */}
        <p style={{ color: mutedColor, fontSize: '0.9rem', marginBottom: '16px' }}>{getWeekRange(selectedWeek)}</p>

        {/* Hours table */}
        <div style={{ backgroundColor: cardBg, borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
                  {['Date', 'Clock In', 'Clock Out', 'Project', 'Hours', 'Break', 'Notes', 'Status'].map(h => (
                    <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', color: mutedColor, letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weekData.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: mutedColor }}>
                      {myHoursLoading ? 'Loading...' : 'No entries for this week'}
                    </td>
                  </tr>
                ) : (
                  weekData.map((r, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}` }}>
                      <td style={{ padding: '12px 16px', color: textColor, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{formatDateShort(r[14])}</td>
                      <td style={{ padding: '12px 16px', color: textColor, fontSize: '0.9rem' }}>{formatTime(r[3])}</td>
                      <td style={{ padding: '12px 16px', color: textColor, fontSize: '0.9rem' }}>{formatTime(r[5])}</td>
                      <td style={{ padding: '12px 16px', color: textColor, fontSize: '0.9rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r[8] || '—'}</td>
                      <td style={{ padding: '12px 16px', color: textColor, fontWeight: '600', fontFamily: 'monospace' }}>{r[7] ? `${r[7]}h` : '—'}</td>
                      <td style={{ padding: '12px 16px', color: textColor, fontSize: '0.9rem' }}>{r[9] ? `${r[9]}m` : '—'}</td>
                      <td style={{ padding: '12px 16px', color: mutedColor, fontSize: '0.85rem', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r[11] || '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: r[12] === 'Complete' ? `${accentSecondary}20` : r[12] === 'Active' ? `${accentPrimary}20` : `${accentError}20`,
                          color: r[12] === 'Complete' ? accentSecondary : r[12] === 'Active' ? accentPrimary : accentError
                        }}>
                          {r[12] || '—'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {weekData.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: `2px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
                    <td colSpan={4} style={{ padding: '14px 16px', fontWeight: '700', color: textColor }}>Weekly Total</td>
                    <td style={{ padding: '14px 16px', fontWeight: '700', color: accentPrimary, fontFamily: 'monospace' }}>{weekTotal.toFixed(2)}h</td>
                    <td style={{ padding: '14px 16px', fontWeight: '600', color: textColor }}>{weekBreakTotal}m</td>
                    <td colSpan={2} style={{ padding: '14px 16px' }}>
                      {anyApproved ? (
                        <span style={{ color: accentSecondary, fontWeight: '500', fontSize: '0.85rem' }}>Approved</span>
                      ) : allConfirmed ? (
                        <span style={{ color: '#f59e0b', fontWeight: '500', fontSize: '0.85rem' }}>Awaiting Approval</span>
                      ) : null}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Confirm button */}
          {weekData.length > 0 && !allConfirmed && weekData.some(r => r[12] === 'Complete') && (
            <div style={{ padding: '16px 20px', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
              <button
                onClick={() => confirmMyHours(selectedWeek)}
                disabled={loading}
                style={{ padding: '12px 24px', backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {loading ? <Loader size={16} /> : <CheckCircle size={16} />}
                Confirm Hours for {selectedWeek}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ========================
  // RENDER: FORMS (unchanged)
  // ========================

  const renderProduction = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Daily Production Log</h2>
        <p style={{ color: mutedColor }}>Record your daily work progress</p>
      </div>

      <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '24px', maxWidth: '600px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Project *</label>
          <select value={productionForm.project} onChange={(e) => setProductionForm({...productionForm, project: e.target.value})} style={formSelectStyle}>
            <option value="">Select Project</option>
            {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Fiber Footage</label>
            <input type="number" value={productionForm.fiberFootage} onChange={(e) => setProductionForm({...productionForm, fiberFootage: e.target.value})} placeholder="0" style={formInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Splices</label>
            <input type="number" value={productionForm.splices} onChange={(e) => setProductionForm({...productionForm, splices: e.target.value})} placeholder="0" style={formInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Drops</label>
            <input type="number" value={productionForm.drops} onChange={(e) => setProductionForm({...productionForm, drops: e.target.value})} placeholder="0" style={formInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Poles</label>
            <input type="number" value={productionForm.poles} onChange={(e) => setProductionForm({...productionForm, poles: e.target.value})} placeholder="0" style={formInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>HDD Footage</label>
            <input type="number" value={productionForm.hddFootage} onChange={(e) => setProductionForm({...productionForm, hddFootage: e.target.value})} placeholder="0" style={formInputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Notes</label>
          <textarea value={productionForm.notes} onChange={(e) => setProductionForm({...productionForm, notes: e.target.value})} rows={3} placeholder="Any delays, issues, or additional notes..." style={{ ...formInputStyle, resize: 'vertical' }} />
        </div>

        <button onClick={handleProductionSubmit} disabled={loading} style={{ width: '100%', padding: '14px', backgroundColor: accentPrimary, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {loading ? <Loader size={18} /> : <CheckCircle size={18} />}
          {loading ? 'Submitting...' : 'Submit Production Log'}
        </button>
      </div>
    </div>
  );

  const renderEquipment = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Equipment Inspection</h2>
        <p style={{ color: mutedColor }}>Pre-use vehicle and equipment check</p>
      </div>

      <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '24px', maxWidth: '600px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Vehicle/Equipment ID *</label>
            <input type="text" value={equipmentForm.vehicleId} onChange={(e) => setEquipmentForm({...equipmentForm, vehicleId: e.target.value})} placeholder="e.g., T-101" style={formInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Mileage/Hours</label>
            <input type="text" value={equipmentForm.mileage} onChange={(e) => setEquipmentForm({...equipmentForm, mileage: e.target.value})} placeholder="Current reading" style={formInputStyle} />
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Safety Checklist</label>
          {['Lights & Signals', 'Tires & Brakes', 'Fluid Levels', 'Safety Equipment', 'Clean Interior'].map((item) => (
            <label key={item} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: textColor, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={equipmentForm.safetyItems.includes(item)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setEquipmentForm({...equipmentForm, safetyItems: [...equipmentForm.safetyItems, item]});
                  } else {
                    setEquipmentForm({...equipmentForm, safetyItems: equipmentForm.safetyItems.filter(i => i !== item)});
                  }
                }}
              />
              {item}
            </label>
          ))}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Issues Found</label>
          <textarea value={equipmentForm.issues} onChange={(e) => setEquipmentForm({...equipmentForm, issues: e.target.value})} rows={3} placeholder="Describe any issues found..." style={{ ...formInputStyle, resize: 'vertical' }} />
        </div>

        <button onClick={handleEquipmentSubmit} disabled={loading} style={{ width: '100%', padding: '14px', backgroundColor: accentSecondary, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {loading ? <Loader size={18} /> : <CheckCircle size={18} />}
          {loading ? 'Submitting...' : 'Submit Inspection'}
        </button>
      </div>
    </div>
  );

  const renderSafety = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Toolbox Talk / Safety Meeting</h2>
        <p style={{ color: mutedColor }}>Document daily safety discussions</p>
      </div>

      <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '24px', maxWidth: '600px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Project *</label>
          <select value={toolboxForm.project} onChange={(e) => setToolboxForm({...toolboxForm, project: e.target.value})} style={formSelectStyle}>
            <option value="">Select Project</option>
            {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Topic *</label>
          <input type="text" value={toolboxForm.topic} onChange={(e) => setToolboxForm({...toolboxForm, topic: e.target.value})} placeholder="e.g., Trench Safety, Heat Illness Prevention" style={formInputStyle} />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Notes</label>
          <textarea value={toolboxForm.notes} onChange={(e) => setToolboxForm({...toolboxForm, notes: e.target.value})} rows={3} placeholder="Key points discussed, hazards identified..." style={{ ...formInputStyle, resize: 'vertical' }} />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Attendees</label>
          <textarea value={toolboxForm.attendees} onChange={(e) => setToolboxForm({...toolboxForm, attendees: e.target.value})} rows={2} placeholder="Names of attendees..." style={{ ...formInputStyle, resize: 'vertical' }} />
        </div>

        <button onClick={handleToolboxSubmit} disabled={loading} style={{ width: '100%', padding: '14px', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {loading ? <Loader size={18} /> : <CheckCircle size={18} />}
          {loading ? 'Submitting...' : 'Sign Toolbox Talk'}
        </button>
      </div>
    </div>
  );

  const renderIncidents = () => (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.75rem', fontWeight: '700', marginBottom: '8px', color: textColor }}>Incident Report</h2>
        <p style={{ color: mutedColor }}>Report safety incidents, near-misses, or property damage</p>
      </div>

      <div style={{ backgroundColor: cardBg, borderRadius: '16px', padding: '24px', maxWidth: '600px' }}>
        <div style={{ backgroundColor: `${accentError}15`, padding: '12px', borderRadius: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={20} color={accentError} />
          <span style={{ color: accentError, fontSize: '0.9rem' }}>For emergencies, call 911 first!</span>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Incident Type *</label>
          <select value={incidentForm.type} onChange={(e) => setIncidentForm({...incidentForm, type: e.target.value})} style={formSelectStyle}>
            <option value="">Select Type</option>
            <option value="Near Miss">Near Miss</option>
            <option value="Property Damage">Property Damage</option>
            <option value="Vehicle Accident">Vehicle Accident</option>
            <option value="Injury">Injury</option>
            <option value="Utility Strike">Utility Strike</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Project/Location</label>
          <select value={incidentForm.project} onChange={(e) => setIncidentForm({...incidentForm, project: e.target.value})} style={formSelectStyle}>
            <option value="">Select Project</option>
            {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: textColor }}>Description *</label>
          <textarea value={incidentForm.description} onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})} rows={4} placeholder="Describe what happened, contributing factors, and any immediate actions taken..." style={{ ...formInputStyle, resize: 'vertical' }} />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: textColor, cursor: 'pointer' }}>
            <input type="checkbox" checked={incidentForm.injuries} onChange={(e) => setIncidentForm({...incidentForm, injuries: e.target.checked})} />
            Were there any injuries?
          </label>
        </div>

        <button onClick={handleIncidentSubmit} disabled={loading} style={{ width: '100%', padding: '14px', backgroundColor: accentError, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {loading ? <Loader size={18} /> : <ShieldAlert size={18} />}
          {loading ? 'Submitting...' : 'Submit Incident Report'}
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'my-hours': return renderMyHours();
      case 'production': return renderProduction();
      case 'equipment': return renderEquipment();
      case 'safety': return renderSafety();
      case 'incidents': return renderIncidents();
      default: return renderDashboard();
    }
  };

  // Fetch my hours when tab is opened
  useEffect(() => {
    if (activeTab === 'my-hours') {
      fetchMyHours();
    }
  }, [activeTab, fetchMyHours]);

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: bgColor,
        display: 'flex',
        WebkitOverflowScrolling: 'touch',
        overflowX: 'hidden'
      }}
      onClick={(e) => { if (e.detail === 3) setShowVersion(!showVersion); }}
    >
      {/* Pulse animation for clocked-in dot */}
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>

      {/* Notification */}
      {submitStatus.show && (
        <div style={{
          position: 'fixed',
          top: isMobile ? '70px' : '20px',
          right: '20px',
          left: isMobile ? '20px' : 'auto',
          padding: '16px 24px',
          backgroundColor: submitStatus.success ? accentSecondary : accentError,
          color: '#fff',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {submitStatus.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
          {submitStatus.message}
        </div>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '56px',
          backgroundColor: cardBg,
          borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          zIndex: 1000
        }}>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            style={{ background: 'none', border: 'none', color: textColor, cursor: 'pointer', padding: '8px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
            <span style={{ color: darkMode ? '#e6c4d9' : '#0a3a7d' }}>ly</span>
            <span style={{ color: darkMode ? '#e6c4d9' : '#2ec7c0' }}>t</span>
            <span style={{ color: mutedColor, fontSize: '0.85rem', marginLeft: '6px' }}>Field</span>
          </div>
          <NotificationBell darkMode={darkMode} user={loggedInUser} setCurrentPage={setCurrentPage} />
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1001 }} />
      )}

      {/* Sidebar */}
      <div style={{
        width: '260px',
        backgroundColor: cardBg,
        borderRight: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        height: '100vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        zIndex: isMobile ? 1002 : 1,
        transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
        transition: isMobile ? 'transform 0.3s ease' : 'none',
        top: 0,
        left: 0
      }}>
        {/* Logo */}
        <div style={{ padding: '20px', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
            <span style={{ color: darkMode ? '#e6c4d9' : '#0a3a7d' }}>ly</span>
            <span style={{ color: darkMode ? '#e6c4d9' : '#2ec7c0' }}>t</span>
            <span style={{ color: mutedColor, fontSize: '0.9rem', marginLeft: '8px' }}>Field</span>
          </div>
          {isMobile && (
            <button onClick={() => setSidebarOpen(false)} aria-label="Close sidebar" style={{ background: 'none', border: 'none', color: mutedColor, cursor: 'pointer', padding: '8px', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <X size={20} />
            </button>
          )}
        </div>

        {/* User Info */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: `${accentPrimary}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentPrimary, fontWeight: '600' }}>
              {loggedInUser?.name?.charAt(0) || 'E'}
            </div>
            <div>
              <div style={{ fontWeight: '500', color: textColor }}>{loggedInUser?.name || 'Employee'}</div>
              <div style={{ fontSize: '0.75rem', color: mutedColor }}>{loggedInUser?.email}</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div style={{ flex: 1, padding: '12px', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                if (item.external) setCurrentPage(item.external);
                else setActiveTab(item.id);
                if (isMobile) setSidebarOpen(false);
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: isMobile ? '14px 16px' : '12px 16px',
                marginBottom: '4px',
                backgroundColor: activeTab === item.id ? `${accentPrimary}15` : 'transparent',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                color: activeTab === item.id ? accentPrimary : textColor,
                textAlign: 'left',
                transition: 'all 0.2s',
                minHeight: isMobile ? '48px' : 'auto'
              }}
            >
              <item.icon size={18} />
              <span style={{ flex: 1, fontSize: '0.9rem' }}>{item.label}</span>
              {item.external && <ChevronRight size={14} color={mutedColor} />}
            </button>
          ))}
        </div>

        {/* Logout */}
        <div style={{ padding: '16px', borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
          <button onClick={handleLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: isMobile ? '14px' : '12px', backgroundColor: 'transparent', border: `1px solid ${accentError}`, borderRadius: '8px', color: accentError, cursor: 'pointer', fontSize: '0.9rem', minHeight: isMobile ? '48px' : 'auto' }}>
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        marginLeft: isMobile ? 0 : '260px',
        padding: isMobile ? '72px 16px 88px' : '32px',
        minHeight: '100vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        {renderContent()}
      </div>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <>
          <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, height: '56px',
            backgroundColor: cardBg, borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-around',
            zIndex: 1000, paddingBottom: 'env(safe-area-inset-bottom)',
          }}>
            {bottomTabs.map(tab => {
              const isActive = tab.id === 'more' ? moreSheetOpen : activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => {
                  if (tab.id === 'more') { setMoreSheetOpen(!moreSheetOpen); }
                  else { setActiveTab(tab.id); setMoreSheetOpen(false); }
                }} style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                  background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0',
                  color: isActive ? accentPrimary : mutedColor,
                }}>
                  <tab.icon size={22} />
                  <span style={{ fontSize: '0.65rem', fontWeight: isActive ? 700 : 500 }}>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* More slide-up sheet */}
          {moreSheetOpen && (
            <>
              <div onClick={() => setMoreSheetOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1001 }} />
              <div style={{
                position: 'fixed', bottom: '56px', left: 0, right: 0, zIndex: 1002,
                backgroundColor: cardBg, borderRadius: '16px 16px 0 0',
                padding: '20px', paddingBottom: '24px',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              }}>
                <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', margin: '0 auto 16px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  {navItems.filter(n => !n.external && !['dashboard', 'my-hours', 'production', 'safety'].includes(n.id)).map(item => (
                    <button key={item.id} onClick={() => { setActiveTab(item.id); setMoreSheetOpen(false); }} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                      padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                      background: activeTab === item.id ? `${accentPrimary}12` : 'transparent',
                      color: activeTab === item.id ? accentPrimary : textColor,
                    }}>
                      <item.icon size={22} />
                      <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>{item.label}</span>
                    </button>
                  ))}
                  <button onClick={() => { setMoreSheetOpen(false); handleLogout(); }} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                    padding: '16px 8px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                    background: 'transparent', color: accentError,
                  }}>
                    <LogOut size={22} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Version Number - Triple Click to Show */}
      {showVersion && (
        <div style={{ position: 'fixed', bottom: isMobile ? '64px' : '10px', right: '10px', fontSize: '0.7rem', opacity: 0.5, color: textColor, backgroundColor: cardBg, padding: '4px 8px', borderRadius: '4px', zIndex: 9999 }}>
          EmployeeDashboard v3.0
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;
